import React from 'react';
import { useTheme } from 'styled-components';
import { stageLabel, stageColor, listNames } from '../../contexts/FarmStatusContext';
import { GlanceCard, HealthRows, StageMix } from './ui';
import { plural, bloomTag, STAGE_NOW, STAGE_SHORT } from './helpers';

/* Day N or the leading stage, the expected stage and harvest countdown, today's changes, and a stage mix per system. */
export default function GrowthCard({ crop }) {
  const theme = useTheme();

  // Growth: the dominant observed stage against the stage the curve expects now.
  const stageCounts = {};
  crop.rows.forEach((r) => { if (r.observed_stage) stageCounts[r.observed_stage] = (stageCounts[r.observed_stage] || 0) + 1; });
  const stageKey = Object.keys(stageCounts).sort((a, b) => stageCounts[b] - stageCounts[a])[0] || null;
  const dated = crop.rows.filter(r => r.drift !== null);
  const behind = dated.filter(r => r.drift < 0);
  const ahead = dated.filter(r => r.drift > 0);
  const growth = {
    stageKey,
    stage: stageKey ? stageLabel(stageKey) : '',
    expected: crop.rows.find(r => r.expected_stage)?.expected_stage || null,
    status: behind.length ? 'warning' : dated.length ? 'normal' : 'offline',
    word: behind.length ? `${plural(behind.length, 'system')} behind` : dated.length ? 'On track' : 'No planting date',
    detail: !dated.length ? `${plural(crop.rows.length, 'system')}, no planting date set`
      : behind.length ? `${listNames(behind.map(r => r.name))} behind schedule`
        : ahead.length ? `${listNames(ahead.map(r => r.name))} ahead, the rest on schedule`
          : `All ${plural(dated.length, 'system')} on schedule`,
  };

  return (
    <GlanceCard to="/calendar" $status={growth.status}>
      <div className="k">Growth <span className="word"><i />{growth.word}</span></div>
      <div className="big">
        {crop.day != null ? `Day ${crop.day}` : (growth.stage || 'No crop')}
        <small>{growth.expected ? `${stageLabel(growth.expected).toLowerCase()} expected`
          : growth.stageKey && crop.total ? `${crop.stages[growth.stageKey] || 0} of ${crop.total} ${STAGE_NOW[growth.stageKey] || growth.stage.toLowerCase()}`
            : crop.rows.length ? 'set the planting date on the Calendar' : ''}</small>
      </div>
      {/* The counts as figures, set like Plant health's and Weather's: what is on the plants
          (each plant's latest check), today's change once someone has checked, and the
          harvest countdown. */}
      {crop.total > 0 && (() => {
        const checked = crop.newToday?.plants_checked > 0;
        const fmt = n => (n || 0).toLocaleString('en-IN');
        const label = (word, n) => (checked ? `${word}, ${n > 0 ? `+${fmt(n)}` : 'none new'} today` : word);
        const cells = [
          [fmt(crop.buds), label(crop.buds === 1 ? 'bud' : 'buds', crop.newToday?.buds)],
          [fmt(crop.flowers), label(crop.flowers === 1 ? 'flower' : 'flowers', crop.newToday?.flowers)],
          ...(crop.fruit > 0 ? [[fmt(crop.fruit), label('fruit', crop.newToday?.fruit)]] : []),
          ...(crop.harvestIn == null ? [] : crop.harvestIn > 0 ? [[plural(crop.harvestIn, 'day'), 'to harvest']] : [['Open', 'harvest window']]),
        ];
        return (
          <div className="facts" style={{ gridTemplateColumns: `repeat(${cells.length}, minmax(0, 1fr))` }}>
            {cells.map(([value, text]) => <div key={text + value}><b>{value}</b><span title={text}>{text}</span></div>)}
          </div>
        );
      })()}
      {/* Only on a day a plant reached a stage or lost buds / flowers; a quiet day says nothing.
          Removed (taken off by the manager, logged as such) is kept apart from lost (dropped). */}
      {(() => {
        const started = [['started_budding', 'budding'], ['started_flowering', 'flowering'], ['started_fruiting', 'fruiting']]
          .filter(([k]) => crop.newToday?.[k] > 0);
        const lost = crop.newToday?.lost || 0;
        const removed = crop.newToday?.removed || 0;
        const gone = [removed > 0 && <><b>{removed}</b> removed</>, lost > 0 && <><b>{lost}</b> lost</>].filter(Boolean);
        return (started.length > 0 || gone.length > 0) && (
          <div className="line">
            {started.map(([k, word], i) => (
              <React.Fragment key={k}>
                {i > 0 && ', '}
                <b>{crop.newToday[k]}</b> {i === 0 ? (crop.newToday[k] === 1 ? 'plant' : 'plants') + ' started ' : ''}{word}
              </React.Fragment>
            ))}
            {started.length > 0 && gone.length > 0 && ' · '}
            {gone.map((g, i) => <React.Fragment key={i}>{i > 0 && ', '}{g}</React.Fragment>)}
            {gone.length > 0 && ' today'}
          </div>
        );
      })()}
      {crop.total > 0 && (
        <StageMix>
          <HealthRows>
            {crop.rows.filter(r => r.plants?.total).map(r => {
              const st = r.plants.stages || {};
              const n = k => (st[k] || 0) + (k === 'establishment' ? st.planting || 0 : 0);
              return (
                <li key={r.system_id} title={`${r.name}: ${crop.order.filter(k => n(k)).map(k => `${n(k)} ${stageLabel(k).toLowerCase()}`).join(', ')}; ${r.plants.buds || 0} buds, ${r.plants.flowers || 0} flowers, ${r.plants.fruit || 0} fruit`}>
                  <span>{r.name}</span>
                  <span className="bar">
                    {crop.order.filter(k => n(k)).map(k => <i key={k} style={{ flex: n(k), background: stageColor(theme, k) }} />)}
                  </span>
                  <span>{bloomTag(r.plants)}</span>
                </li>
              );
            })}
          </HealthRows>
          <div className="key" title="Plants at each stage, each counted at its effective stage">
            {crop.order.filter(st => crop.stages[st] || st === growth.expected).map(st => (
              <span key={st} className={crop.stages[st] ? '' : 'due'}>
                <i style={{ background: stageColor(theme, st) }} />{STAGE_SHORT[st] || stageLabel(st)} <b>{crop.stages[st] || 0}</b>
              </span>
            ))}
          </div>
        </StageMix>
      )}
    </GlanceCard>
  );
}
